import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useState
} from "react";
import { useApplicationContext } from "contexts/ApplicationContext";
import { useStompClient } from "hooks/use-stomp-client";
import { apiJWT } from "api/api-jwt";
import { makeEvent } from "services/events";
import {
  AlertItem,
  APINotification,
  NotificationContextProps,
  NotificationItem,
  WSMessage
} from "./types";

const notificationContext = createContext<NotificationContextProps>({
  notifications: [],
  alerts: [],
  unreadCount: 0,
  markAllAsRead: () => undefined,
  markAsRead: () => Promise.resolve(),
  clearNotifications: () => undefined,
  isLoading: false
});

export let modalTutorialPipelineData: Partial<APINotification> = {};

export const NotificationContextProvider = ({
  children
}: {
  children: ReactNode;
}) => {
  const { user } = useApplicationContext();
  const [notifications, setNotifications] = useState<NotificationItem[]>([]);
  const [alerts, setAlerts] = useState<AlertItem[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const { connect, disconnect, subscribe, connected } = useStompClient({
    url: async () => {
      const accessToken = await apiJWT.getValidAccessToken();
      if (!accessToken || !process.env.REACT_APP_PUBLIC_NOTIFICATION_WEBSOCKET)
        return "";
      return (
        process.env.REACT_APP_PUBLIC_NOTIFICATION_WEBSOCKET +
        "?access_token=" +
        accessToken
      );
    }
  });

  const handleMessage = useCallback((body: string) => {
    try {
      const message: WSMessage = JSON.parse(body);

      console.log(
        "Notification STOMP: nova mensagem recebida",
        JSON.stringify(message)
      );

      if (message.type === "NOTIFICATION") {
        const item: NotificationItem = {
          ...message,
          id: message.payload.id,
          read: message.isRead
        };
        setNotifications((prev) => [item, ...prev]);
      } else if (message.type === "ALERT") {
        const alert: AlertItem = {
          id: message.payload.id,
          type: "ALERT",
          payload: message.payload,
          sentAt: message.sentAt,
          expiresAt: message.expiresAt
        };
        setAlerts((prev) => [alert, ...prev]);

        makeEvent("open-alerts-modal");
      }
    } catch (e) {
      console.error("Notification STOMP: erro ao processar mensagem", e);
    }
  }, []);

  const fetchNotifications = useCallback(async () => {
    setIsLoading(true);
    try {
      const { data } = await apiJWT.get<APINotification[]>("/notifications/");

      data.forEach((n) => {
        if (n.tipoEnvio === "ALERT_PIPELINE") {
          modalTutorialPipelineData = n;
        }
      });

      setNotifications(
        data
          .filter((m) => m.tipoEnvio === "NOTIFICATION")
          .map(
            (n): NotificationItem => ({
              type: "NOTIFICATION",
              payload: { id: n.id, titulo: n.titulo, mensagem: n.mensagem },
              sentAt: n.sentAt,
              expiresAt: n.expiresAt,
              isRead: n.lida,
              id: n.id,
              read: n.lida
            })
          )
      );

      setAlerts(
        data
          .filter((m) => m.tipoEnvio === "ALERT" && !m.lida)
          .map(
            (n): AlertItem => ({
              id: n.id,
              type: "ALERT",
              payload: { id: n.id, titulo: n.titulo, mensagem: n.mensagem },
              sentAt: n.sentAt,
              expiresAt: n.expiresAt
            })
          )
      );

      console.log(
        "Notification REST: notificações carregadas",
        JSON.stringify(data)
      );
    } catch (e) {
      console.error("Notification REST: erro ao buscar notificações", e);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!connected) return;

    const { customerId, userId, type: userType } = user.userData;

    subscribe("/topic/notifications/all", (message) => {
      handleMessage(message.body);
    });

    if (customerId) {
      subscribe(`/topic/notifications/customer/${customerId}`, (message) => {
        handleMessage(message.body);
      });

      if (userId) {
        subscribe(
          `/topic/notifications/customer/${customerId}/user/${userId}`,
          (message) => {
            handleMessage(message.body);
          }
        );
      }

      if (userType) {
        subscribe(
          `/topic/notifications/customer/${customerId}/user-type/${userType}`,
          (message) => {
            handleMessage(message.body);
          }
        );
      }
    }
  }, [connected, user.userData, subscribe, handleMessage]);

  useEffect(() => {
    if (user.isLogged) {
      fetchNotifications();
      connect();
    } else {
      disconnect();
      setNotifications([]);
    }

    return () => {
      disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user.isLogged]);

  const markAllAsRead = useCallback(() => {
    setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
  }, []);

  const markAsRead = useCallback(async (id: number) => {
    setIsLoading(true);
    try {
      await apiJWT.get(`/notifications/read/${id}`);
      setNotifications((prev) =>
        prev.map((n) => (n.id === id ? { ...n, read: true } : n))
      );
      await fetchNotifications();
    } catch (e) {
      console.error("Notification REST: erro ao marcar como lida", e);
      setNotifications((prev) =>
        prev.map((n) => (n.id === id ? { ...n, read: false } : n))
      );
    } finally {
      setIsLoading(false);
    }
  }, []);

  const clearNotifications = useCallback(() => {
    setNotifications([]);
  }, []);

  const unreadCount = notifications.filter((n) => !n.read).length;

  return (
    <notificationContext.Provider
      value={{
        notifications,
        alerts,
        unreadCount,
        markAllAsRead,
        markAsRead,
        clearNotifications,
        isLoading
      }}
    >
      {children}
    </notificationContext.Provider>
  );
};

export const useNotificationContext = () => {
  const context = useContext(notificationContext);
  if (context === undefined)
    throw new Error(
      "useNotificationContext must be used within a NotificationContextProvider"
    );
  return context;
};
